/* SPEC-145 — the tour engine (no new dependency).
 *
 * A lightweight custom overlay: a dimmed backdrop with a spotlight cut-out on
 * the current target's bounding rect, and a floating card that narrates the
 * step. It drives the real app — navigating to each step's existing deep link
 * and spotlighting the real element found via `data-tour="<key>"`.
 *
 * Robustness contract (SPEC-145): a missing anchor is SKIPPED gracefully in the
 * direction of travel — never a blank spotlight. Steps whose page/tab the user
 * can't see are filtered out before the tour starts (see config/tours.ts), so
 * the step counter is honest per role.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Tour, TourStep, TOURS, visibleSteps } from '@/config/tours';
import { TourCard } from './TourCard';

interface TourContextValue {
  /** Open the picker modal. */
  openPicker: () => void;
  /** Start a specific tour by id (resumes at the last-viewed step). */
  startTour: (tourId: string) => void;
  pickerOpen: boolean;
  closePicker: () => void;
  activeTourId: string | null;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within a TourProvider');
  return ctx;
}

const LS_PREFIX = 'suryaiq_tour_';
/** How long to wait for an anchor to render before skipping the step (ms). */
const ANCHOR_TIMEOUT_MS = 2200;
const SPOTLIGHT_PADDING = 8;

function loadLastStep(tourId: string): number {
  try {
    const raw = localStorage.getItem(LS_PREFIX + tourId);
    const n = raw == null ? 0 : parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function saveLastStep(tourId: string, step: number) {
  try {
    localStorage.setItem(LS_PREFIX + tourId, String(step));
  } catch {
    /* private mode / quota — progress memory is best-effort */
  }
}

/** Does the current location already match a step's route (path + every param it names)?
 *
 *  SPEC-364: this used to compare `?tab=` ONLY. A step that names a second param — the
 *  by-SKU promotions step names `promoView=sku` — then counted as "already here" and the
 *  engine skipped the navigation, so the step narrated a view the planner was not looking
 *  at. Compare EVERY param the step declares instead.
 *
 *  Params the current URL carries but the step does NOT name are ignored, so a step still
 *  matches a deep link that carries extra context (e.g. `&product_code=…`) and the engine
 *  does not navigate that context away. A step that must pin a param therefore has to name
 *  it — which is why the by-promotion step names `promoView=promotion`. */
function sameLocation(route: string | undefined, pathname: string, search: string): boolean {
  if (!route) return true;
  const [p, q = ''] = route.split('?');
  if ((p || '/') !== pathname) return false;
  const have = new URLSearchParams(search);
  for (const [key, want] of new URLSearchParams(q)) {
    if ((have.get(key) || null) !== (want || null)) return false;
  }
  return true;
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeTour, setActiveTour] = useState<Tour | null>(null);
  const [steps, setSteps] = useState<TourStep[]>([]);
  const [index, setIndex] = useState(0);

  // Spotlight geometry for the current step. null rect = centered card (no target).
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [ready, setReady] = useState(false);
  const targetRef = useRef<Element | null>(null);
  // A monotonically increasing token so a stale async resolve can't apply.
  const resolveToken = useRef(0);
  // Direction of travel, so a skipped (missing) anchor advances the right way.
  const directionRef = useRef<1 | -1>(1);

  const endTour = useCallback(() => {
    resolveToken.current += 1;
    setActiveTour(null);
    setSteps([]);
    setRect(null);
    setReady(false);
    targetRef.current = null;
  }, []);

  const goTo = useCallback((next: number, dir: 1 | -1) => {
    directionRef.current = dir;
    setIndex(next);
  }, []);

  const startTour = useCallback(
    (tourId: string) => {
      const tour = TOURS.find((t) => t.id === tourId);
      // startTour is only called with ids from the picker, which lists availableTours;
      // guard anyway so a bad id is a no-op, not a crash.
      if (!tour) return;
      const vis = visibleSteps(tour, user);
      if (vis.length === 0) return;
      const saved = loadLastStep(tourId);
      // Resume at the saved step, but if it was the final one treat the tour as
      // finished and restart from the top.
      const start = saved >= vis.length - 1 ? 0 : saved;
      setPickerOpen(false);
      setActiveTour(tour);
      setSteps(vis);
      directionRef.current = 1;
      setIndex(start);
    },
    [user],
  );

  // ── Step resolution: navigate, then wait for the anchor (or skip) ──────────
  useEffect(() => {
    if (!activeTour) return;
    const step = steps[index];
    // Ran off either end → tour complete.
    if (!step) {
      endTour();
      return;
    }

    saveLastStep(activeTour.id, index);
    const token = ++resolveToken.current;
    setReady(false);
    targetRef.current = null;

    const needsNav = !sameLocation(step.route, location.pathname, location.search);
    if (step.route && needsNav) navigate(step.route);

    // A step with no anchor is a centered card — show immediately.
    if (!step.anchor) {
      setRect(null);
      setReady(true);
      return;
    }

    const anchor = step.anchor;
    const deadline = Date.now() + ANCHOR_TIMEOUT_MS;
    let timer: number | undefined;

    const probe = () => {
      if (token !== resolveToken.current) return;
      // SPEC-462: an anchor can exist twice — the desktop rail and a mobile-only control
      // share a key — and one copy is always inside a display:none breakpoint wrapper.
      // Take the first copy that is actually laid out; a copy with no client rects is
      // invisible, and spotlighting it would draw an empty box at 0,0. None laid out =
      // not found, so the step skips gracefully (SPEC-145 honesty rule).
      const el = Array.from(document.querySelectorAll(`[data-tour="${anchor}"]`))
        .find((node) => node.getClientRects().length > 0);
      if (el) {
        // Instant (not smooth) so the layout has settled by the time we measure —
        // a smooth scroll is still animating on the next frame, which would place
        // the card against a stale rect (the anchor's pre-scroll position, often
        // below the fold → card off the bottom with scrolling blocked). SPEC-147.
        el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'instant' as ScrollBehavior });
        requestAnimationFrame(() => {
          if (token !== resolveToken.current) return;
          targetRef.current = el;
          setRect(el.getBoundingClientRect());
          setReady(true);
        });
        return;
      }
      if (Date.now() > deadline) {
        if (import.meta.env?.DEV) {
          // eslint-disable-next-line no-console
          console.warn(`[tour] anchor "${anchor}" not found — skipping step ${index + 1}`);
        }
        const dir = directionRef.current;
        // Skipping backward off the front recovers forward rather than exiting the
        // tour (index would go negative → step lookup fails → unintended end).
        if (dir === -1 && index === 0) {
          goTo(1, 1);
        } else {
          goTo(index + dir, dir);
        }
        return;
      }
      timer = window.setTimeout(probe, 80);
    };

    // Give a freshly navigated route a moment to mount before the first probe.
    timer = window.setTimeout(probe, needsNav ? 140 : 0);
    return () => {
      if (timer) window.clearTimeout(timer);
    };
    // location.pathname/search intentionally excluded: navigation is an effect of
    // the step, not an input to it — re-running on our own navigate() would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTour, index, steps]);

  // Keep the spotlight glued to the target as the page scrolls or resizes.
  useEffect(() => {
    if (!ready || !targetRef.current) return;
    const el = targetRef.current;
    const update = () => setRect(el.getBoundingClientRect());
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, [ready, index]);

  const next = useCallback(() => goTo(index + 1, 1), [goTo, index]);
  const back = useCallback(() => goTo(index - 1, -1), [goTo, index]);

  // Global keyboard control while a tour runs.
  useEffect(() => {
    if (!activeTour) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        endTour();
      } else if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        if (index > 0) back();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [activeTour, endTour, next, back, index]);

  const value = useMemo<TourContextValue>(
    () => ({
      openPicker: () => setPickerOpen(true),
      closePicker: () => setPickerOpen(false),
      startTour,
      pickerOpen,
      activeTourId: activeTour?.id ?? null,
    }),
    [startTour, pickerOpen, activeTour],
  );

  const currentStep = activeTour ? steps[index] : undefined;

  return (
    <TourContext.Provider value={value}>
      {children}
      {activeTour && (
        <>
          {/* Keep the backdrop up while a step resolves (navigating / waiting for
              its anchor) so the screen never blanks between steps; the spotlight
              cut-out + card appear once the target is measured. Esc still exits
              even mid-resolve (handled by the document-level key listener). */}
          <TourSpotlight rect={ready ? rect : null} />
          {ready && currentStep && (
            <TourCard
              step={currentStep}
              index={index}
              total={steps.length}
              tourTitle={activeTour.title}
              rect={rect}
              onNext={next}
              onBack={back}
              onExit={endTour}
            />
          )}
        </>
      )}
    </TourContext.Provider>
  );
}

/* ── Spotlight ──────────────────────────────────────────────────────────────
 * Two layers: a transparent full-screen catcher that blocks page interaction,
 * and a positioned box whose huge box-shadow dims everything EXCEPT its own
 * rect (the cut-out). With no rect the catcher itself carries the dim. */
function TourSpotlight({ rect }: { rect: DOMRect | null }) {
  return (
    <>
      {/* Click-catcher — blocks the page underneath (the card sits above this). */}
      <div
        className="fixed inset-0 z-[60]"
        style={{ background: rect ? 'transparent' : 'rgba(9, 9, 11, 0.72)' }}
        aria-hidden="true"
      />
      {rect && (
        <div
          className="fixed z-[61] rounded-lg pointer-events-none transition-all duration-200 ease-out"
          style={{
            top: rect.top - SPOTLIGHT_PADDING,
            left: rect.left - SPOTLIGHT_PADDING,
            width: rect.width + SPOTLIGHT_PADDING * 2,
            height: rect.height + SPOTLIGHT_PADDING * 2,
            boxShadow: '0 0 0 9999px rgba(9, 9, 11, 0.72)',
            outline: '2px solid rgb(245 158 11)',
            outlineOffset: '2px',
          }}
          aria-hidden="true"
        />
      )}
    </>
  );
}
